import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '@symphenia/ui-components';
import { erfuelltRecht } from '@symphenia/shared-types';
import { mitAnpassung, zettelLage, type TagErgebnis } from '@symphenia/payroll-core';
import { useAuthStore } from '@/lib/auth.store';
import { bestaetige } from '@/lib/dialoge';
import { leseZahl, useWarnungBeimVerlassen } from '@/lib/speichern';
import {
  KiAuswahl,
  KiEinleitung,
  KiEntscheidungZelle,
  KiFuss,
  KiLesenKnopf,
  KiLiest,
  KiTabelle,
  KiVorschlag,
  KiZustand,
  kiFarbe,
  kiGelb,
  kiTd,
  kiTh,
} from '@/components/ki-vorschlag/KiVorschlag';
import {
  applySlipProposal,
  discardSlipProposal,
  fetchSlipProposal,
  fetchSlipReading,
  readSlip,
  setSlipReading,
  type ZettelAnpassung,
  type ZettelFelder,
  type ZettelLesenStand,
  type ZettelVorschlagDaten,
  type ZettelVorschlagTag,
} from '../api/payroll.api';
import { leseUhrzeit, pruefeUhrzeit } from '../lib/uhrzeit';

const FELD_TEXT: Record<string, string> = {
  startTime: 'Kommt',
  endTime: 'Geht',
  breakMinutes: 'Pause',
  travelHours: 'Reise',
  departure: 'Abfahrt',
  isDriver: 'Lenker',
  overnight: 'Nächtigung',
  absenceTypeId: 'Abwesenheit',
  absenceScope: 'Umfang',
  absences: 'Abwesenheit',
  note: 'Bemerkung',
};
const WOCHENTAG = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const SUMME_TEXT: Record<string, string> = { stunden: 'Gesamt', naz: 'NAZ', uest: 'ÜST', za: 'ZA', rz: 'RZ', mz: 'MZ', ez: 'EZ', naechtigungen: 'N' };
/** Spalten, die nur erscheinen, wenn der Zettel sie an irgendeinem Tag trägt. */
const WAHLSPALTEN: { feld: keyof ZettelFelder; titel: string }[] = [
  { feld: 'travelHours', titel: 'Reise' },
  { feld: 'departure', titel: 'Abf.' },
  { feld: 'isDriver', titel: 'Lkr' },
  { feld: 'overnight', titel: 'Näch' },
];

function zahl(n: number | null | undefined): string {
  return n === null || n === undefined ? '' : n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function tagText(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  return `${WOCHENTAG[d.getDay()]} ${iso.slice(8, 10)}.`;
}

const FEHLZEIT = ['absenceTypeId', 'absenceScope', 'absences'];

/**
 * Zu entscheiden ist ein Tag, der etwas trägt, nicht entschieden ist und nicht schon so im
 * Raster steht — außer, er ist im Fenster angepasst: Dann vermerkt das Übernehmen die Anpassung.
 */
export function zuEntscheiden(t: ZettelVorschlagTag): boolean {
  return t.entscheidung === null && Object.keys(t.felder).length > 0 && (t.lage !== 'SAME' || t.angepasst.length > 0);
}

/** Anpassen lässt sich, was noch nicht entschieden ist — nur nicht, was schon so im Raster steht. */
export function anpassbar(gelesen: ZettelVorschlagTag): boolean {
  return gelesen.entscheidung === null && !(gelesen.lage === 'SAME' && Object.keys(gelesen.felder).length > 0);
}

/**
 * Der Tag, wie er mit den Anpassungen des Fensters gilt: Werte, Hinweise und die Lage zum Raster —
 * gerechnet mit denselben Regeln wie am Server (`mitAnpassung`, `zettelLage` aus dem Kern).
 */
export function mitAnpassungen(gelesen: ZettelVorschlagTag, anpassung: ZettelAnpassung | undefined): ZettelVorschlagTag {
  if (!anpassung || !anpassbar(gelesen)) return gelesen;
  const t = mitAnpassung(gelesen, anpassung);
  return t.angepasst.length ? { ...t, ...zettelLage(t.felder, gelesen.vorhanden) } : gelesen;
}

/** Vorausgewählt wird, was neu ist und an dem nichts unsicher gelesen wurde. */
export function sicher(t: ZettelVorschlagTag): boolean {
  return zuEntscheiden(t) && t.lage === 'NEW' && t.unsicher.length === 0;
}

/** Der Grund in der Spalte „Hinweis" — sobald der Tag entschieden ist, nur noch, was angepasst wurde. */
export function hinweisText(t: ZettelVorschlagTag): string {
  const felder = (liste: string[]) => [...new Set(liste.map((f) => FELD_TEXT[f] ?? f))].join(', ');
  const angepasst = t.angepasst.length ? `angepasst: ${felder(t.angepasst)}` : null;
  if (t.entscheidung) return angepasst ?? '';
  return [
    angepasst,
    t.unsicher.length ? `unsicher gelesen: ${felder(t.unsicher)}` : null,
    t.lage === 'DIFFERENT' ? `im Raster steht etwas anderes: ${felder(t.abweichend)}` : null,
    ...t.hinweise,
  ]
    .filter(Boolean)
    .join(' · ');
}

// ── Eingaben der Vorschlagstabelle ───────────────────────────────────────────
// Jede liefert den Wert, `null` für „leer" und `undefined` für „so nicht lesbar" — das bleibt rot
// am Feld stehen und ändert den Vorschlag nicht.

const liesZeit = (text: string): string | null | undefined => {
  const w = leseUhrzeit(text);
  return w === null ? null : pruefeUhrzeit(w) ? undefined : w;
};
const liesMinuten = (text: string): number | null | undefined => {
  const t = text.trim();
  if (t === '') return null;
  return /^\d{1,4}$/.test(t) && Number(t) <= 1440 ? Number(t) : undefined;
};
const liesStunden = (text: string): number | null | undefined => {
  if (text.trim() === '') return null;
  const n = leseZahl(text);
  return n === null || n < 0 || n > 24 ? undefined : Math.round(n * 100) / 100 || null;
};
const liesText = (text: string): string | null | undefined => {
  const t = text.trim();
  return t === '' ? null : t.length > 500 ? undefined : t;
};
const stundenEingabe = (n: number | undefined): string => (n === undefined ? '' : String(n).replace('.', ','));

// Schmal wie der Text, den sie ersetzen — die Tabelle hat einen Monat Tage und wenig Platz neben dem Zettel.
// Die gestrichelte Linie sagt „hier lässt sich schreiben", ohne jede Zelle zum Kasten zu machen.
const eingabe =
  'rounded-none border-0 border-b border-dashed border-muted-foreground/50 bg-transparent px-0.5 py-0 text-xs text-inherit focus:border-solid focus:border-primary focus:bg-background focus:outline-none aria-[invalid=true]:border-solid aria-[invalid=true]:border-destructive aria-[invalid=true]:text-destructive';
const auswahl = 'rounded border border-border/70 bg-background px-0.5 py-0 text-xs text-inherit focus:border-primary focus:outline-none';

/**
 * Ein Wert des Vorschlags zum Anpassen. Er gilt beim Verlassen des Felds (oder mit Enter), Esc
 * stellt den geltenden Wert wieder her. Gespeichert wird hier nichts — die Anpassung geht mit
 * „Übernehmen" (`.claude/rules/speichern.md`, Fall 3).
 */
function Eingabe<W extends string | number>({
  label,
  wert,
  breite,
  lies,
  onWert,
}: {
  label: string;
  /** Der geltende Wert als Text. */
  wert: string;
  breite: string;
  lies: (text: string) => W | null | undefined;
  onWert: (w: W | null) => void;
}) {
  const [text, setText] = useState(wert);
  const [getippt, setGetippt] = useState(false);
  const [fehler, setFehler] = useState(false);
  // Solange nicht getippt wird, folgt das Feld dem geltenden Wert — auch die Pause, die den Zeiten folgt.
  useEffect(() => {
    if (!getippt) setText(wert);
  }, [wert, getippt]);

  const gilt = () => {
    if (!getippt) return;
    const w = lies(text);
    if (w === undefined) return setFehler(true);
    setFehler(false);
    setGetippt(false);
    onWert(w);
  };

  return (
    <input
      aria-label={label}
      aria-invalid={fehler || undefined}
      title={fehler ? 'So nicht lesbar — der Vorschlag bleibt, wie er war.' : undefined}
      className={`${eingabe} ${breite}`}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        setGetippt(true);
        setFehler(false);
      }}
      onBlur={gilt}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') {
          setText(wert);
          setGetippt(false);
          setFehler(false);
        }
      }}
    />
  );
}

/**
 * Die Fehlzeit eines Tages zum Anpassen: Art, Umfang (ganz · halb · in Stunden) und die Stunden.
 * Geändert wird immer das Ganze — Stunden gelten statt Art und Umfang, und umgekehrt.
 */
function FehlzeitFeld({
  tag,
  felder,
  arten,
  onWert,
}: {
  tag: string;
  felder: ZettelFelder;
  arten: { id: string; code: string }[];
  onWert: (a: ZettelAnpassung) => void;
}) {
  const std = felder.absences?.[0];
  const art = std?.absenceTypeId ?? felder.absenceTypeId ?? '';
  const umfangOhneStunden = felder.absenceScope ?? (felder.startTime && felder.endTime ? 'HALF' : 'FULL');
  // „Std." ist gewählt, die Stunden fehlen noch — bis sie dastehen, gilt der bisherige Umfang.
  const [stundenOffen, setStundenOffen] = useState(false);
  const umfang = std || stundenOffen ? 'STD' : umfangOhneStunden;

  const gib = (artId: string, u: string, stunden: number | null) => {
    if (!artId) return onWert({ absenceTypeId: null, absenceScope: null, absences: null });
    if (u === 'STD' && stunden !== null) return onWert({ absenceTypeId: null, absenceScope: null, absences: [{ absenceTypeId: artId, hours: stunden }] });
    onWert({ absenceTypeId: artId, absenceScope: u === 'STD' ? umfangOhneStunden : (u as 'FULL' | 'HALF'), absences: null });
  };

  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <select
        aria-label={`${tag} Abwesenheit`}
        className={auswahl}
        value={art}
        onChange={(e) => {
          if (!e.target.value) setStundenOffen(false);
          gib(e.target.value, umfang, std?.hours ?? null);
        }}
      >
        <option value="">—</option>
        {arten.map((a) => (
          <option key={a.id} value={a.id}>
            {a.code}
          </option>
        ))}
      </select>
      {art && (
        <select
          aria-label={`${tag} Umfang der Abwesenheit`}
          className={auswahl}
          value={umfang}
          onChange={(e) => {
            setStundenOffen(e.target.value === 'STD');
            gib(art, e.target.value, std?.hours ?? null);
          }}
        >
          <option value="FULL">ganz</option>
          <option value="HALF">halb</option>
          <option value="STD">Std.</option>
        </select>
      )}
      {art && umfang === 'STD' && (
        <Eingabe
          label={`${tag} Stunden der Abwesenheit`}
          breite="w-9 tabular-nums"
          wert={stundenEingabe(std?.hours)}
          lies={liesStunden}
          onWert={(h) => {
            setStundenOffen(false);
            if (h !== null) gib(art, 'STD', h);
            else if (std) gib(art, umfangOhneStunden, null);
          }}
        />
      )}
    </span>
  );
}

const LESEFEHLER: Record<string, string> = {
  PAYROLL_SLIP_READING_OFF: 'Das Lesen von Abgabezetteln ist nicht eingeschaltet.',
  PAYROLL_SLIP_AI_UNAVAILABLE: 'Für diese Firma ist keine KI eingerichtet.',
};

interface Props {
  userId: string;
  jahr: number;
  monat: number;
  fileId: string;
  /** Kürzel der Fehlzeit-Arten für die Anzeige. */
  arten: { id: string; code: string }[];
  /** Was die Rechnung aus dem Raster macht — neben die Werte vom Zettel gestellt. */
  ergebnis: TagErgebnis[];
  darfUebernehmen: boolean;
  /** Zettel pflegen (`payroll.slip.manage`) — Voraussetzung für „Erneut lesen". */
  darfLesen: boolean;
  /** Kennung des Änderungsvorgangs im fertiggestellten Monat. */
  aenderung?: string;
  onUebernommen: () => void;
  onSchliessen: () => void;
}

/**
 * Der gelesene Abgabezettel als **Vorschlag** (AP-22 Teil 2, I) — im selben Fenster wie die
 * KI-Lesung der Eingangsrechnung (`components/ki-vorschlag`, Teil 6, V).
 *
 * Nichts davon ist eine Tageszeile, bis es übernommen wird. Unsicher Gelesenes ist gelb und
 * trägt seinen Grund; ein Tag, an dem schon etwas anderes steht, wird nur überschrieben, wenn
 * er ausdrücklich angehakt ist. Jeder Tag wird übernommen oder verworfen — beides wird
 * gemerkt, bis der Zettel neu gelesen wird. Was der Mitarbeiter selbst gerechnet hat, steht
 * als Kontrollwert daneben und wird nie übernommen.
 *
 * **Die Werte lassen sich vor dem Übernehmen anpassen** — hat die KI „16 00" statt „16 30"
 * gelesen, wird es hier berichtigt, nicht erst im Raster. Die Anpassung gilt im Fenster und geht
 * mit „Übernehmen" (ein Vorgang, ein Knopf); die Pause folgt geänderten Zeiten nach der Regel des
 * Lesens. Ein übernommener Tag zeigt danach, was übernommen wurde, mit dem Vermerk „angepasst".
 */
export function ZettelVorschlag({
  userId,
  jahr,
  monat,
  fileId,
  arten,
  ergebnis,
  darfUebernehmen,
  darfLesen,
  aenderung,
  onUebernommen,
  onSchliessen,
}: Props) {
  const [daten, setDaten] = useState<ZettelVorschlagDaten | null>(null);
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [liest, setLiest] = useState(false);
  // Der Zettel daneben ist noch nie gelesen worden (beim Blättern zwischen mehreren Zetteln).
  const [ungelesen, setUngelesen] = useState(false);
  // „Erneut lesen" gibt es nur, wo die Firma das Lesen eingeschaltet hat und die KI eingerichtet ist.
  const [lesenAn, setLesenAn] = useState(false);
  // Was am Vorschlag angepasst ist, je Tag — gilt im Fenster und geht mit „Übernehmen".
  const [anpassungen, setAnpassungen] = useState<Record<string, ZettelAnpassung>>({});
  useWarnungBeimVerlassen(Object.keys(anpassungen).length > 0);

  useEffect(() => {
    if (!darfLesen) return setLesenAn(false);
    let aus = false;
    fetchSlipReading()
      .then((s) => !aus && setLesenAn(s.enabled && s.aiAvailable))
      .catch(() => !aus && setLesenAn(false));
    return () => {
      aus = true;
    };
  }, [darfLesen]);

  /**
   * Einen Stand zeigen: frisch gelesen mit Vorauswahl und ohne Anpassungen; sonst bleibt angehakt
   * und angepasst, was noch nicht entschieden ist.
   */
  const zeige = useCallback((v: ZettelVorschlagDaten, vorwahl: boolean) => {
    const unentschieden = (d: string) => v.tage.some((t) => t.date === d && t.entscheidung === null);
    setDaten(v);
    setUngelesen(false);
    setAuswahl((alt) => (vorwahl ? new Set(v.tage.filter(sicher).map((t) => t.date)) : new Set([...alt].filter(unentschieden))));
    setAnpassungen((alt) => (vorwahl ? {} : Object.fromEntries(Object.entries(alt).filter(([d]) => unentschieden(d)))));
  }, []);

  useEffect(() => {
    let aus = false;
    setDaten(null);
    setUngelesen(false);
    setError(null);
    setMeldung(null);
    setAnpassungen({});
    fetchSlipProposal(userId, jahr, monat, fileId)
      .then((v) => !aus && zeige(v, true))
      .catch((err) => {
        if (aus) return;
        if ((err as { response?: { status?: number } })?.response?.status === 404) setUngelesen(true);
        else setError('Der Vorschlag konnte nicht geladen werden.');
      });
    return () => {
      aus = true;
    };
  }, [userId, jahr, monat, fileId, zeige]);

  const code = useMemo(() => new Map(arten.map((a) => [a.id, a.code])), [arten]);
  const gerechnet = useMemo(() => new Map(ergebnis.map((t) => [t.datum, t])), [ergebnis]);
  const spalten = useMemo(
    () => WAHLSPALTEN.filter((s) => daten?.tage.some((t) => t.felder[s.feld] !== undefined || t.unsicher.includes(s.feld))),
    [daten],
  );

  // Die Tage, wie sie mit den Anpassungen gelten — daraus folgt alles, was das Fenster zeigt und sendet.
  const tage = useMemo(() => (daten?.tage ?? []).map((t) => mitAnpassungen(t, anpassungen[t.date])), [daten, anpassungen]);
  const offen = tage.filter(zuEntscheiden).length;
  const gewaehlt = tage.filter((t) => auswahl.has(t.date) && zuEntscheiden(t));

  const schalte = (date: string) =>
    setAuswahl((alt) => {
      const neu = new Set(alt);
      if (neu.has(date)) neu.delete(date);
      else neu.add(date);
      return neu;
    });

  /**
   * Einen Wert des Vorschlags anpassen. Entspricht der Tag danach wieder dem Gelesenen, ist die
   * Anpassung weg. Wer einen Tag anpasst, hat ihn angesehen: Er ist danach angehakt — außer, im
   * Raster steht etwas anderes; Überschreiben bleibt eine ausdrückliche Entscheidung.
   */
  const passeAn = (gelesen: ZettelVorschlagTag, teil: ZettelAnpassung) => {
    const anpassung = { ...anpassungen[gelesen.date], ...teil };
    const danach = mitAnpassungen(gelesen, anpassung);
    const bleibt = danach.angepasst.length > 0;
    setMeldung(null);
    setAnpassungen((alt) => {
      const neu = { ...alt };
      if (bleibt) neu[gelesen.date] = anpassung;
      else delete neu[gelesen.date];
      return neu;
    });
    if (bleibt && zuEntscheiden(danach) && danach.lage !== 'DIFFERENT') setAuswahl((alt) => new Set(alt).add(gelesen.date));
  };

  const schliesse = async () => {
    if (
      Object.keys(anpassungen).length > 0 &&
      !(await bestaetige('Am Vorschlag ist etwas angepasst, das noch nicht übernommen ist. Beim Schließen geht es verloren.', { ok: 'Schließen' }))
    )
      return;
    onSchliessen();
  };

  const uebernimm = async () => {
    if (!daten || gewaehlt.length === 0) return;
    setLaeuft(true);
    setError(null);
    setMeldung(null);
    try {
      const dates = gewaehlt.map((t) => t.date);
      // Ein angehakter Tag, an dem schon etwas anderes steht, ist die ausdrückliche Nennung.
      const overwrite = gewaehlt.filter((t) => t.lage === 'DIFFERENT').map((t) => t.date);
      const corrections = dates.filter((d) => anpassungen[d]).map((d) => ({ date: d, fields: anpassungen[d] }));
      const r = await applySlipProposal(userId, jahr, monat, fileId, corrections.length ? { dates, overwrite, corrections } : { dates, overwrite }, aenderung);
      setMeldung(`${r.uebernommen.length} ${r.uebernommen.length === 1 ? 'Tag' : 'Tage'} übernommen.`);
      onUebernommen();
      zeige(await fetchSlipProposal(userId, jahr, monat, fileId), false);
    } catch (err) {
      const c = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      setError(
        c === 'PAYROLL_MONTH_FINAL' || c === 'PAYROLL_MONTH_CHANGE_REQUIRED'
          ? 'Der Monat ist fertiggestellt — übernehmen geht hier nur über „Monat ändern".'
          : 'Die Tage konnten nicht übernommen werden.',
      );
    } finally {
      setLaeuft(false);
    }
  };

  const verwirf = async (was: { dates: string[] } | { all: true }, fehler: string) => {
    setLaeuft(true);
    setError(null);
    setMeldung(null);
    try {
      zeige(await discardSlipProposal(userId, jahr, monat, fileId, was), false);
    } catch {
      setError(fehler);
    } finally {
      setLaeuft(false);
    }
  };

  const lies = async (erneut: boolean) => {
    if (
      erneut &&
      !(await bestaetige('Den Zettel noch einmal lesen? Der bisherige Vorschlag wird ersetzt — auch, was daran verworfen oder angepasst wurde.', { ok: 'Erneut lesen' }))
    )
      return;
    setLiest(true);
    setError(null);
    setMeldung(null);
    try {
      zeige(await readSlip(userId, jahr, monat, fileId), true);
    } catch (err) {
      const antwort = (err as { response?: { status?: number; data?: { code?: string } } })?.response;
      setError(
        (antwort?.data?.code && LESEFEHLER[antwort.data.code]) ??
          (antwort?.status === 429 ? 'Das KI-Kontingent ist aufgebraucht — der Zettel wurde nicht gelesen.' : 'Der Zettel konnte nicht gelesen werden.'),
      );
    } finally {
      setLiest(false);
    }
  };

  const lesen = lesenAn ? <KiLesenKnopf erneut={!ungelesen} disabled={laeuft || liest} onClick={() => void lies(!ungelesen)} /> : null;

  const wortlaut = (w: unknown): string => (w === null || w === undefined || w === '' ? 'nichts' : w === true ? 'angekreuzt' : String(w));

  const zelle = (t: ZettelVorschlagTag, feld: string, inhalt: ReactNode, key?: string) => {
    const gruppe = FEHLZEIT.includes(feld) ? FEHLZEIT : [feld];
    const unsicher = t.unsicher.includes(feld) && !t.entscheidung;
    const anders = t.abweichend.includes(feld) && !t.entscheidung;
    const angepasst = gruppe.some((f) => t.angepasst.includes(f));
    return (
      <td
        key={key}
        className={`${kiTd} tabular-nums ${unsicher ? kiGelb : ''} ${angepasst ? `font-medium ${kiFarbe}` : anders ? 'font-medium text-foreground' : ''}`}
        title={
          [
            unsicher ? 'unsicher gelesen — bitte am Zettel prüfen' : null,
            angepasst ? `angepasst${gruppe.length === 1 ? ` — gelesen war: ${wortlaut(t.gelesen[feld])}` : ''}` : null,
            anders ? `im Raster steht: ${wortlaut(t.vorhanden?.[feld])}` : null,
          ]
            .filter(Boolean)
            .join(' · ') || undefined
        }
      >
        {inhalt}
        {anders && <span className="ml-0.5 text-amber-600">*</span>}
      </td>
    );
  };

  /** Eine der Spalten, die nur erscheinen, wenn der Zettel sie trägt — zum Anpassen, solange der Tag offen ist. */
  const wahlzelle = (t: ZettelVorschlagTag, gelesen: ZettelVorschlagTag, feld: keyof ZettelFelder, titel: string, bearbeitbar: boolean) => {
    const w = t.felder[feld];
    const name = `${tagText(t.date)} ${FELD_TEXT[feld] ?? titel}`;
    if (!bearbeitbar) return zelle(t, feld, typeof w === 'boolean' ? (w ? '✕' : '') : typeof w === 'number' ? zahl(w) : ((w as string | undefined) ?? ''), feld);
    if (feld === 'isDriver' || feld === 'overnight')
      return zelle(t, feld, <input type="checkbox" aria-label={name} checked={w === true} onChange={(e) => passeAn(gelesen, { [feld]: e.target.checked ? true : null })} />, feld);
    if (feld === 'travelHours')
      return zelle(
        t,
        feld,
        <Eingabe label={name} breite="w-9 tabular-nums" wert={stundenEingabe(w as number | undefined)} lies={liesStunden} onWert={(n) => passeAn(gelesen, { travelHours: n })} />,
        feld,
      );
    return zelle(t, feld, <Eingabe label={name} breite="w-10 tabular-nums" wert={(w as string | undefined) ?? ''} lies={liesZeit} onWert={(z) => passeAn(gelesen, { [feld]: z })} />, feld);
  };

  const summen = daten ? Object.entries(daten.summen).filter(([, w]) => w !== null) : [];
  const zeigtTage = !!daten && !liest && daten.lesbar && daten.tage.length > 0;

  return (
    <KiVorschlag
      titel="KI-Vorschlag — Abgabezettel"
      marke={zeigtTage && offen > 0 && 'noch nicht im Raster'}
      aktionen={
        <button type="button" className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Vorschlag schließen" onClick={() => void schliesse()}>
          <X size={16} />
        </button>
      }
    >
      {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
      {meldung && <p className="rounded-md border border-green-600/30 bg-green-600/10 px-3 py-2 text-sm text-foreground">{meldung}</p>}
      {!daten && !error && !ungelesen && !liest && <KiZustand>Laden…</KiZustand>}
      {ungelesen && !liest && <KiZustand aktion={lesen}>Dieser Zettel ist noch nicht gelesen.</KiZustand>}
      {liest && <KiLiest text="Die KI liest den Zettel">Das dauert bis zu einer Minute.</KiLiest>}

      {daten && !liest && !daten.lesbar && (
        <KiZustand aktion={lesen}>Nicht lesbar{daten.grund ? ` — ${daten.grund}` : ''}. Bitte erfasse den Monat von Hand.</KiZustand>
      )}
      {daten && !liest && daten.lesbar && !daten.kopf.monatPasst && (
        <KiZustand aktion={lesen}>
          Auf dem Zettel steht {daten.kopf.monat ? `Monat ${daten.kopf.monat}` : 'ein anderer Monat'}
          {daten.kopf.jahr ? `/${daten.kopf.jahr}` : ''} — geöffnet ist {monat}/{jahr}. Gehört der Zettel zu einem anderen Monat? Dann hinterlege ihn dort;
          vorgeschlagen wird hier nichts.
        </KiZustand>
      )}
      {daten && !liest && daten.lesbar && daten.kopf.monatPasst && daten.tage.length === 0 && (
        <KiZustand aktion={lesen}>Auf dem Zettel wurde kein ausgefüllter Tag gefunden.</KiZustand>
      )}
      {daten && !liest && daten.lesbar && daten.kopf.namePasst === false && (
        <p className="flex items-start gap-1.5 text-sm text-amber-700 dark:text-amber-300">
          <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
          Der Name auf dem Zettel („{daten.kopf.name}") passt nicht zum Mitarbeiter dieses Monats — bitte prüfen.
        </p>
      )}

      {zeigtTage && daten && (
        <>
          <KiEinleitung>
            Ins Raster kommt erst, was du übernimmst. Gelb heißt: nicht sicher gelesen, mit Grund. Ein Stern heißt: Im Raster steht etwas anderes —
            anhaken überschreibt.
            {darfUebernehmen && offen > 0 && ' Die Werte lassen sich hier anpassen, bevor du übernimmst — die Pause folgt geänderten Zeiten.'}
            {offen > 0 ? ` Noch ${offen} offen.` : ' Der Vorschlag ist abgearbeitet — alles ist übernommen, verworfen oder steht schon so im Raster.'}
          </KiEinleitung>

          <KiTabelle
            dicht
            kopf={
              <>
                <th className="w-8 px-2 py-1.5" />
                <th className={kiTh}>Tag</th>
                <th className={kiTh}>Kommt</th>
                <th className={kiTh}>Geht</th>
                <th className={kiTh} title="(Geht − Kommt) − Gesamtarbeitsstunden des Zettels, in Minuten">
                  Pause
                </th>
                {spalten.map((s) => (
                  <th key={s.feld} className={kiTh}>
                    {s.titel}
                  </th>
                ))}
                <th className={kiTh}>Abwesenheit</th>
                <th className={kiTh}>Bemerkung</th>
                <th className={`${kiTh} text-right`} title="Gesamtarbeitsstunden laut Zettel / Netto-Arbeitszeit, wie das Raster sie rechnet">
                  Std. Zettel / gerechnet
                </th>
                <th className={`${kiTh} text-right`} title="Überstunden laut Zettel / Mehrarbeit und Überstunden, wie das Raster sie rechnet">
                  ÜST Zettel / gerechnet
                </th>
                <th className={`${kiTh} min-w-[9rem]`}>Hinweis</th>
                <th className={kiTd} />
              </>
            }
          >
            {tage.map((t, i) => {
              const gelesen = daten.tage[i];
              const bearbeitbar = darfUebernehmen && anpassbar(gelesen);
              const name = tagText(t.date);
              const g = gerechnet.get(t.date);
              const imRaster = t.lage === 'SAME';
              const erledigt = imRaster && !zuEntscheiden(t);
              const stdAnders = imRaster && g && t.kontrolle.stunden !== null && Math.abs(g.naz - t.kontrolle.stunden) >= 0.01;
              const uest = g ? g.mehrarbeitTeilzeit + g.mehrarbeitVollzeit + g.ueberstunden : null;
              const uestAnders = imRaster && uest !== null && t.kontrolle.uest !== null && Math.abs(uest - t.kontrolle.uest) >= 0.01;
              const leer = Object.keys(t.felder).length === 0;
              return (
                <tr key={t.date} className="border-b border-border align-top last:border-0">
                  <KiAuswahl
                    label={`${tagText(t.date)} übernehmen`}
                    zeigen={darfUebernehmen && zuEntscheiden(t)}
                    checked={auswahl.has(t.date)}
                    onChange={() => schalte(t.date)}
                  />
                  <td className={`whitespace-nowrap ${kiTd} font-medium text-foreground`}>{tagText(t.date)}</td>
                  {zelle(
                    t,
                    'startTime',
                    bearbeitbar ? (
                      <Eingabe label={`${name} Kommt`} breite="w-10 tabular-nums" wert={t.felder.startTime ?? ''} lies={liesZeit} onWert={(z) => passeAn(gelesen, { startTime: z })} />
                    ) : (
                      (t.felder.startTime ?? '')
                    ),
                  )}
                  {zelle(
                    t,
                    'endTime',
                    bearbeitbar ? (
                      <Eingabe label={`${name} Geht`} breite="w-10 tabular-nums" wert={t.felder.endTime ?? ''} lies={liesZeit} onWert={(z) => passeAn(gelesen, { endTime: z })} />
                    ) : (
                      (t.felder.endTime ?? '')
                    ),
                  )}
                  {zelle(
                    t,
                    'breakMinutes',
                    bearbeitbar ? (
                      <Eingabe
                        label={`${name} Pause`}
                        breite="w-7 tabular-nums"
                        wert={t.felder.breakMinutes === undefined ? '' : String(t.felder.breakMinutes)}
                        lies={liesMinuten}
                        onWert={(m) => passeAn(gelesen, { breakMinutes: m })}
                      />
                    ) : (
                      (t.felder.breakMinutes ?? '')
                    ),
                  )}
                  {spalten.map((s) => wahlzelle(t, gelesen, s.feld, s.titel, bearbeitbar))}
                  {zelle(
                    t,
                    t.felder.absences ? 'absences' : 'absenceTypeId',
                    bearbeitbar && (t.felder.absences?.length ?? 0) <= 1 ? (
                      <>
                        <FehlzeitFeld tag={name} felder={t.felder} arten={arten} onWert={(a) => passeAn(gelesen, a)} />
                        {t.fehlzeitWort && <span className="ml-1 text-muted-foreground">„{t.fehlzeitWort}"?</span>}
                      </>
                    ) : t.felder.absences ? (
                      t.felder.absences.map((a) => `${code.get(a.absenceTypeId) ?? '?'} ${zahl(a.hours)} h`).join(', ')
                    ) : t.felder.absenceTypeId ? (
                      `${code.get(t.felder.absenceTypeId) ?? '?'}${t.felder.absenceScope === 'HALF' ? ' ½' : ''}`
                    ) : t.fehlzeitWort ? (
                      `„${t.fehlzeitWort}"?`
                    ) : (
                      ''
                    ),
                  )}
                  {zelle(
                    t,
                    'note',
                    bearbeitbar ? (
                      <Eingabe label={`${name} Bemerkung`} breite="w-20" wert={t.felder.note ?? ''} lies={liesText} onWert={(n) => passeAn(gelesen, { note: n })} />
                    ) : (
                      (t.felder.note ?? '')
                    ),
                  )}
                  <td className={`whitespace-nowrap ${kiTd} text-right tabular-nums ${stdAnders ? `${kiGelb} text-foreground` : 'text-muted-foreground'}`}>
                    {zahl(t.kontrolle.stunden)}
                    {imRaster && g && t.kontrolle.stunden !== null ? ` / ${zahl(g.naz)}` : ''}
                  </td>
                  <td className={`whitespace-nowrap ${kiTd} text-right tabular-nums ${uestAnders ? `${kiGelb} text-foreground` : 'text-muted-foreground'}`}>
                    {zahl(t.kontrolle.uest)}
                    {imRaster && uest !== null && (t.kontrolle.uest !== null || uest > 0) ? ` / ${zahl(uest)}` : ''}
                  </td>
                  <td className={`${kiTd} text-muted-foreground`}>{hinweisText(t)}</td>
                  <KiEntscheidungZelle
                    entscheidung={t.entscheidung}
                    darf={darfUebernehmen}
                    disabled={laeuft}
                    haftet
                    sonst={t.entscheidung ? undefined : erledigt ? 'steht schon so da' : leer ? '' : undefined}
                    onVerwerfen={() => void verwirf({ dates: [t.date] }, 'Der Tag ließ sich nicht verwerfen.')}
                  />
                </tr>
              );
            })}
          </KiTabelle>

          {darfUebernehmen && offen > 0 ? (
            <KiFuss
              anzahl={gewaehlt.length}
              laeuft={laeuft}
              onUebernehmen={() => void uebernimm()}
              onSichere={() => setAuswahl(new Set(tage.filter(sicher).map((t) => t.date)))}
              onRestVerwerfen={() => void verwirf({ all: true }, 'Der Rest ließ sich nicht verwerfen.')}
              lesen={lesen}
            />
          ) : (
            lesen
          )}
        </>
      )}

      {daten && !liest && (summen.length > 0 || daten.diverses) && (
        <div className="space-y-1 text-xs text-muted-foreground">
          {summen.length > 0 && (
            <p>
              <span className="font-medium text-foreground">Summen laut Zettel</span> (Kontrollwerte, werden nicht übernommen):{' '}
              {summen.map(([s, w]) => `${SUMME_TEXT[s] ?? s} ${zahl(w)}`).join(' · ')}
            </p>
          )}
          {daten.diverses && (
            <p>
              <span className="font-medium text-foreground">Diverses:</span> {daten.diverses}
            </p>
          )}
        </div>
      )}
    </KiVorschlag>
  );
}

/**
 * Einstellung „Abgabezettel lesen lassen" (AP-22 Teil 2, I).
 *
 * Aus bei jedem Mandanten. Einschalten darf, wer `payroll.slip.configure` hat —
 * der Administrator der Firma, nicht die Rolle „Lohnverrechnung": Der Zettel
 * geht mit Namen, Arbeitszeiten und Fehlzeiten an die KI. Der Hinweis dazu wird
 * beim Einschalten bestätigt; sein Text und seine Fassung (`lohnzettel-ki/1`)
 * kommen vom Server, der auch festhält, wer wann welcher Fassung zugestimmt hat.
 */
export function ZettelLesenEinstellung() {
  const rechte = useAuthStore((s) => s.user?.permissions);
  const darfLesen = erfuelltRecht('payroll.slip.read', rechte);
  const darfAendern = erfuelltRecht('payroll.slip.configure', rechte);
  const [stand, setStand] = useState<ZettelLesenStand | null>(null);
  const [bestaetigt, setBestaetigt] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!darfLesen) return;
    fetchSlipReading()
      .then(setStand)
      .catch(() => setError('Die Einstellung konnte nicht geladen werden.'));
  }, [darfLesen]);

  if (!darfLesen) return null;

  const setze = async (enabled: boolean) => {
    setError(null);
    try {
      setStand(await setSlipReading(enabled, enabled ? bestaetigt : undefined));
      setBestaetigt(false);
    } catch {
      setError('Die Einstellung konnte nicht gespeichert werden.');
    }
  };

  return (
    <div className="space-y-3 rounded-lg border border-border bg-card p-4 shadow-sm">
      <div>
        <h2 className="text-sm font-semibold text-foreground">Abgabezettel lesen lassen</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Die KI liest einen hinterlegten Abgabezettel und schlägt die Tageszeilen vor; übernommen wird nur, was du bestätigst
          {darfAendern ? '.' : ' — die Einstellung ändert der Administrator der Firma.'}
        </p>
      </div>
      {stand && !stand.aiAvailable && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          Für diese Firma ist keine KI eingerichtet (eigener Schlüssel oder Kontingent von Symphenia). Ohne sie lässt sich kein Zettel lesen.
        </p>
      )}
      {stand?.enabled ? (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-foreground">
            Eingeschaltet
            {stand.acceptedAt ? ` — Hinweis zum Datenschutz bestätigt am ${new Date(stand.acceptedAt).toLocaleDateString('de-AT')}` : ''}.
          </span>
          {darfAendern && (
            <Button size="sm" variant="outline" onClick={() => void setze(false)}>
              Ausschalten
            </Button>
          )}
        </div>
      ) : (
        stand && (
          <div className="space-y-2">
            <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-foreground">
              <strong>Hinweis zum Datenschutz:</strong> {stand.consentText}
            </p>
            {darfAendern && (
              <>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 accent-[hsl(var(--primary))]"
                    checked={bestaetigt}
                    onChange={(e) => setBestaetigt(e.target.checked)}
                  />
                  <span>{stand.consentCheckbox}</span>
                </label>
                <Button size="sm" disabled={!bestaetigt} onClick={() => void setze(true)}>
                  Einschalten
                </Button>
              </>
            )}
          </div>
        )
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
